import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaStar, FaUsers, FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getMembershipsByLocalId, deleteMembership } from '../../../../../../crud/clients_api/memberships';
import { NestedMembership, FlatMembership } from '../../types/membership.types';
import { flattenMemberships, formatCurrency } from '../../utils/membershipUtils';
import CreateMembership from './components/CreateMembership/CreateMembership';
import UpdateMembership from './components/UpdateMembership/UpdateMembership';
import DeleteMembership from './components/DeleteMembership/DeleteMembership';
import LoadingSpinner from '../../../../../../components/common/LoadingSpinner';
import './Memberships.scss';

const Memberships: React.FC = () => {
  const [nestedMemberships, setNestedMemberships] = useState<NestedMembership[]>([]);
  const [flatMemberships, setFlatMemberships] = useState<FlatMembership[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState<FlatMembership | null>(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const localId = Number(user.local_id) || 0;

  const fetchMemberships = async () => {
    setLoading(true);
    try {
      const response: any = await getMembershipsByLocalId(localId);
      const nested = response.data as NestedMembership[];
      setNestedMemberships(nested);
      const flattened = flattenMemberships(nested);
      setFlatMemberships(flattened);
    } catch (error: any) {
      console.error('Error fetching memberships:', error);
      toast.error('Error al cargar las membresías');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (localId) {
      fetchMemberships();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localId]);

  const handleEdit = (membership: FlatMembership) => {
    setSelectedMembership(membership);
    setShowEditModal(true);
  };

  const handleDelete = (membership: FlatMembership) => {
    setSelectedMembership(membership);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedMembership) return;

    try {
      await deleteMembership(selectedMembership.membership_id);
      toast.success('Membresía eliminada correctamente');
      setShowDeleteModal(false);
      setSelectedMembership(null);
      fetchMemberships();
    } catch (error: any) {
      console.error('Error deleting membership:', error);
      const message = error?.message || 'Error al eliminar la membresía';
      toast.error(message);
    }
  };

  const renderMembershipCard = (membership: FlatMembership) => {
    return (
      <div
        key={membership.membership_id}
        className="membership-card menu-box like-menu"
      >
        <div className="card-top-row">
          <div className="membership-info">
            <h2 className="membership-title">
              {membership.membership_name}
              {membership.is_default && (
                <span className="default-badge" title="Membresía por defecto">
                  <FaStar />
                </span>
              )}
            </h2>
            <div className="membership-badges">
              <span className="discount-badge">
                {membership.discount_percentage}% descuento
              </span>
              <span className="clients-badge">
                <FaUsers /> {membership.client_count} cliente{membership.client_count !== 1 ? 's' : ''}
              </span>
            </div>
          </div>
          <div className="card-actions">
            <button
              className="icon-btn"
              onClick={() => handleEdit(membership)}
              title="Editar"
            >
              <FaEdit />
            </button>
            <button
              className={`icon-btn danger ${!membership.can_delete ? 'disabled' : ''}`}
              onClick={() => membership.can_delete && handleDelete(membership)}
              title={membership.can_delete ? 'Eliminar' : 'No se puede eliminar (tiene clientes asignados)'}
              disabled={!membership.can_delete}
            >
              <FaTrash />
            </button>
          </div>
        </div>

        {membership.membership_description && (
          <p className="membership-desc">{membership.membership_description}</p>
        )}

        {membership.next_membership_id && (
          <div className="progression-info">
            <FaArrowRight className="progression-icon" />
            <span className="progression-text">
              Gasta {formatCurrency(membership.next_level_required_spent || 0)} para alcanzar{' '}
              <strong>{membership.next_level_name}</strong>
            </span>
          </div>
        )}

        {!membership.can_delete && (
          <div className="warning-banner">
            <FaExclamationTriangle />
            <span>No se puede eliminar mientras tenga clientes asignados</span>
          </div>
        )}
      </div>
    );
  };

  const renderEmptyState = () => (
    <div className="empty-state">
      <div className="empty-content">
        <FaPlus className="empty-icon" />
        <h3>¡Crea tu primera membresía!</h3>
        <p>
          Las membresías te permiten fidelizar clientes con descuentos y beneficios exclusivos.
        </p>
        <button
          className="create-button aesthetic"
          onClick={() => setShowCreateModal(true)}
        >
          <FaPlus />
          Crear membresía
        </button>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSpinner text="Cargando membresías..." transparent />;
  }

  return (
    <div className="memberships-container">
      {flatMemberships.length > 0 ? (
        <>
          <div className="table-header">
            <button
              className="create-button"
              onClick={() => setShowCreateModal(true)}
            >
              <FaPlus />
              Crear membresía
            </button>
          </div>

          <div className="memberships-list">
            {flatMemberships.map(renderMembershipCard)}
          </div>
        </>
      ) : (
        renderEmptyState()
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateMembership
          show={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          onSuccess={fetchMemberships}
          existingMemberships={flatMemberships}
        />
      )}

      {showEditModal && selectedMembership && (
        <UpdateMembership
          show={showEditModal}
          handleClose={() => {
            setShowEditModal(false);
            setSelectedMembership(null);
          }}
          membership={selectedMembership}
          onSuccess={fetchMemberships}
          existingMemberships={flatMemberships}
        />
      )}

      {showDeleteModal && selectedMembership && (
        <DeleteMembership
          show={showDeleteModal}
          handleClose={() => {
            setShowDeleteModal(false);
            setSelectedMembership(null);
          }}
          membership={selectedMembership}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default Memberships;